import React, { useState, useContext, useCallback } from "react";
import {
  Box,
  Button,
  DialogActions,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { awaitableUploadToS3 } from "../../utils/S3Utils";
import { getBpm } from "../../utils/bpmUtil";
import {
  S3CredentialsContext,
  S3CredentialsContextInterface,
} from "../../contexts";
import { AuthContext, AuthContextInterface } from "../../contexts";
import {
  UserDetailsContext,
  UserDetailsContextInterface,
} from "../../contexts";
import {
  FileState,
  Folder,
  PostFolderResponse,
  ProjectFile,
  TrackProcessorInfo,
} from "../../proto/api_pb";
import { RpcError } from "grpc-web";
import { postTrackBatch } from "../../services/trackService";
import { postFolder } from "../../services/folderService";
import { FileStateReverseMap } from "../SingleProject/types";
import { postProjectFiles } from "../../services/projectService";
import { addItemsModalStyles, batchTrackAddModalStyles } from "./styles";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import Mixpanel from "../../utils/mixpanel";
import CustomButton from "../CustomButton/CustomButton";
import { FolderUploadProps } from "./types";
import { toast } from "react-toastify";
import { FolderUploadInfo, FolderFileItem } from "../FileUploadStatusCard/types";

const ASSETS_URL = process.env.REACT_APP_ASSET_S3_BUCKET_URL!;
const BATCH_SIZE = 10;

const FolderUpload: React.FC<FolderUploadProps> = ({
  setIsUploading,
  handleCloseBatchTrackAddModal,
  setIsUploadProcessing,
  setFolderUploadInfo,
  handleOpenMessageModal,
}) => {
  const { getValidAccessToken } = useGetValidAccessToken();
  const [filesFromFolder, setFilesFromFolder] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [generalFilesFromFolder, setGeneralFilesFromFolder] = useState<File[]>(
    []
  );
  const [folderToUpload, setFolderToUpload] = useState<string>("");
  const { userId } = useContext<AuthContextInterface>(AuthContext);
  const { enterprise, isEnterpriseAccount } =
    useContext<UserDetailsContextInterface>(UserDetailsContext);
  const { awsAccessKeyId, awsSecretAccessKey, awsSessionToken } =
    useContext<S3CredentialsContextInterface>(S3CredentialsContext);

  const handleProgress = useCallback((pNum: number, fileName: string, currentBatch: number) => {
    setFolderUploadInfo((prevInfo) => {
      if (!prevInfo) return prevInfo;

      const updatedContents = prevInfo.folderContents?.map(item => {
        const sanitizedItemName = sanitizeName(item.name);
        const sanitizedFileName = fileName;
        if (sanitizedItemName === sanitizedFileName ||
          sanitizedItemName.endsWith('/' + sanitizedFileName)) {
          return {
            ...item,
            progress: pNum,
            isUploading: pNum < 100,
            isUploaded: pNum === 100
          };
        }
        return item;
      }) || [];

      return {
        ...prevInfo,
        currentFileName: fileName,
        currentProgress: pNum,
        currentBatch: currentBatch,
        folderContents: updatedContents,
        uploadedFiles: pNum === 100 ? prevInfo.uploadedFiles + 1 : prevInfo.uploadedFiles
      };
    });
  }, [setFolderUploadInfo]);

  const sanitizeName = (name: string) => {
    const withoutEmoji = name.replace(/([\uD800-\uDBFF][\uDC00-\uDFFF])/g, "_");
    // eslint-disable-next-line no-control-regex
    const withUnderscores = withoutEmoji.replace(/[^\x00-\xFF]/g, "_");
    return withUnderscores.replace(/_{2,}/g, "_");
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const audioFiles: File[] = [];
    const generalFiles: File[] = [];
    Array.from(files).forEach((file) => {
      if (file.type.startsWith("audio/")) {
        audioFiles.push(file);
      } else {
        generalFiles.push(file);
      }
    });

    if (audioFiles.length === 0 && generalFiles.length === 0) return;

    setFilesFromFolder(audioFiles);
    setGeneralFilesFromFolder(generalFiles);

    const firstFile = files[0];
    if (firstFile && "webkitRelativePath" in firstFile) {
      const originalFolderName = firstFile.webkitRelativePath.split("/")[0];
      const sanitizedFolderName = sanitizeName(originalFolderName);

      setFolderToUpload(sanitizedFolderName);
    }
  };

  const handleClearFolder = () => {
    setFilesFromFolder([]);
    setGeneralFilesFromFolder([]);
    setFolderToUpload("");
  };
  const handleClose = () => {
    setFolderToUpload("");
    handleCloseBatchTrackAddModal();
  };

  // Process folders and create them in the backend
  const processAndCreateFolders = async (allFiles: File[]) => {
    const folderIdMap = new Map<string, string>();
    const uniqueFolders = new Set<string>();

    allFiles.forEach((file) => {
      const webkitRelativePath = (file as any).webkitRelativePath;
      if (!webkitRelativePath) return;

      const pathParts = webkitRelativePath.split("/");
      pathParts.pop();
      let currentPath = "";
      for (const part of pathParts) {
        const sanitizedPart = sanitizeName(part);
        currentPath = currentPath
          ? `${currentPath}/${sanitizedPart}`
          : sanitizedPart;
        uniqueFolders.add(currentPath);
      }
    });

    const sortedFolders = Array.from(uniqueFolders).sort(
      (a, b) => a.split("/").length - b.split("/").length
    );

    for (const folderPath of sortedFolders) {
      const folderName = folderPath.split("/").pop() ?? "";
      const parentFolderPath =
        folderPath.split("/").slice(0, -1).join("/") || null;

      const folder = new Folder();
      folder.setName(folderName);
      folder.setFolderState(FileState.FILE_STATE_ACTIVE);
      folder.setOwnerId(userId);

      if (enterprise?.id) {
        folder.setEnterpriseId(enterprise.id);
      }

      if (parentFolderPath && folderIdMap.has(parentFolderPath)) {
        folder.setParentFolderId(folderIdMap.get(parentFolderPath)!);
      }

      try {
        const token = await getValidAccessToken();
        const res = await postFolder(token, folder);

        if (res instanceof PostFolderResponse) {
          const folderId = res.getFolder()?.getId();
          if (folderId) {
            folderIdMap.set(folderPath, folderId);
          }
        }
      } catch (error) {
        console.error("Error creating folder:", error);
      }
    }

    return folderIdMap;
  };

  // Process audio files in batches
  const processAudioFiles = async (fileArray: File[], folderIdMap: Map<string, string>, currentBatchNumber: number) => {
    const tracksProcessorInfoList: TrackProcessorInfo[] = [];
    const uploadPromises = [];

    for (const file of fileArray) {
      const webkitRelativePath = (file as any).webkitRelativePath;
      if (!webkitRelativePath) continue;

      const pathParts = webkitRelativePath.split("/");
      const sanitizedFileName = sanitizeName(pathParts.pop() || '');
      const parentFolderId = folderIdMap.get(pathParts.map(sanitizeName).join("/")) ?? null;
      const sanitizedFile = new File([file], sanitizedFileName, { type: file.type });

      try {
        const uploadPromise = awaitableUploadToS3(
          userId,
          sanitizedFile,
          `song`,
          file.type,
          awsAccessKeyId,
          awsSecretAccessKey,
          awsSessionToken,
          (progress) => handleProgress(progress, sanitizedFileName, currentBatchNumber)
        ).then(async (songKey) => {
          const songUrl = `${ASSETS_URL}/${songKey}`;
          const newTrack = new TrackProcessorInfo();
          newTrack.setFilename(sanitizedFileName);
          newTrack.setTrackUrl(songUrl);
          newTrack.setParentFolderIdList([parentFolderId ?? ""]);

          let bpm = 0;
          try {
            bpm = await getBpm(sanitizedFile);
          } catch (error) {
            console.log("BPM detection Error Batch Upload", error);
            bpm = 0;
          }

          newTrack.setBpm(bpm);
          tracksProcessorInfoList.push(newTrack);
        });

        uploadPromises.push(uploadPromise);
      } catch (error) {
        console.error("Error uploading audio file:", error);
      }
    }

    try {
      await Promise.all(uploadPromises);

      if (tracksProcessorInfoList.length > 0) {
        const token = await getValidAccessToken();
        const postBatchTrackResponse = await postTrackBatch(
          tracksProcessorInfoList,
          "",
          "",
          [],
          [],
          userId,
          "",
          "",
          "",
          token,
          isEnterpriseAccount && enterprise?.id ? enterprise.id : undefined
        );

        if (postBatchTrackResponse instanceof RpcError) {
          throw postBatchTrackResponse;
        }
      }
    } catch (error) {
      console.error("Error processing audio files:", error);
    }
  };

  // Process general files in batches
  const processGeneralFiles = async (generalFilesArray: File[], folderIdMap: Map<string, string>, currentBatchNumber: number) => {
    const generalFilesUploadPromises = [];
    const uploadedFiles: ProjectFile[] = [];

    for (const file of generalFilesArray) {
      const webkitRelativePath = (file as any).webkitRelativePath;
      if (!webkitRelativePath) continue;

      const parts = webkitRelativePath.split("/");
      const fileName = parts.pop() || '';
      const sanitizedFileName = sanitizeName(fileName);
      const sanitizedFile = new File([file], sanitizedFileName, { type: file.type });
      const parentFolderId = folderIdMap.get(parts.map(sanitizeName).join("/")) ?? null;

      try {
        const uploadPromise = awaitableUploadToS3(
          userId,
          sanitizedFile,
          `project-file`,
          file.type,
          awsAccessKeyId,
          awsSecretAccessKey,
          awsSessionToken,
          (progress) => handleProgress(progress, sanitizedFileName, currentBatchNumber)
        ).then(async (fileKey) => {
          const fileUrl = `${ASSETS_URL}/${fileKey}`;
          const newFile = new ProjectFile();
          newFile.setFileName(sanitizedFileName);
          newFile.setFileUrl(fileUrl);
          newFile.setFileState(
            FileStateReverseMap[FileState.FILE_STATE_ACTIVE]
          );
          newFile.setFileSize(file.size);
          newFile.setOwnerId(userId);
          newFile.setProjectId("");

          if (enterprise?.id) {
            newFile.setEnterpriseId(enterprise.id);
          }

          newFile.setParentFolderIdList([parentFolderId ?? ""]);
          newFile.setType(file.type);
          uploadedFiles.push(newFile);
        });

        generalFilesUploadPromises.push(uploadPromise);
      } catch (error) {
        console.error("Error uploading general file:", error);
      }
    }

    try {
      await Promise.all(generalFilesUploadPromises);

      if (uploadedFiles.length > 0) {
        const newToken = await getValidAccessToken();
        const res = await postProjectFiles(newToken, uploadedFiles);

        if (res instanceof RpcError) {
          throw res;
        }
      }
    } catch (error) {
      console.error("Error processing general files:", error);
    }
  };

  // Creates batches of files and processes them
  const processFilesInBatches = async (audioFiles: File[], generalFiles: File[], folderIdMap: Map<string, string>) => {
    const allFiles = [...audioFiles, ...generalFiles];
    const totalBatches = Math.ceil(allFiles.length / BATCH_SIZE);
    setFolderUploadInfo((prevInfo) => {
      if (!prevInfo) return prevInfo;
      return {
        ...prevInfo,
        totalBatches: totalBatches
      };
    });

    // Process files in batches of BATCH_SIZE
    for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
      const startIdx = batchIndex * BATCH_SIZE;
      const endIdx = Math.min(startIdx + BATCH_SIZE, allFiles.length);
      const currentBatchFiles = allFiles.slice(startIdx, endIdx);
      setFolderUploadInfo((prevInfo) => {
        if (!prevInfo) return prevInfo;
        return {
          ...prevInfo,
          currentBatch: batchIndex + 1,
          filesInCurrentBatch: currentBatchFiles.length
        };
      });

      // Split current batch into audio and general files
      const batchAudioFiles = currentBatchFiles.filter(file => file.type.startsWith("audio/"));
      const batchGeneralFiles = currentBatchFiles.filter(file => !file.type.startsWith("audio/"));

      // Process audio files in current batch
      if (batchAudioFiles.length > 0) {
        await processAudioFiles(batchAudioFiles, folderIdMap, batchIndex + 1);
      }

      // Process general files in current batch
      if (batchGeneralFiles.length > 0) {
        await processGeneralFiles(batchGeneralFiles, folderIdMap, batchIndex + 1);
      }

    }
  };

  const handleFolderUpload = async () => {
    if (folderToUpload === "") {
      toast.warn("Please select folder to upload");
      return;
    }
    setIsLoading(true);
    setIsUploading(true);
    setIsUploadProcessing(true);
    handleCloseBatchTrackAddModal();

    const fileArray = Array.from(filesFromFolder);
    const generalFilesArray = Array.from(generalFilesFromFolder);
    const allFiles = [...fileArray, ...generalFilesArray];

    const firstFile = allFiles[0];
    const firstFilePathParts = (firstFile as any).webkitRelativePath.split("/");
    const rootFolderName = firstFilePathParts[0];

    // Create folder contents list for tracking individual files
    const folderContents: FolderFileItem[] = allFiles.map(file => {
      const fileName = file.name;
      const relativePath = (file as any).webkitRelativePath;
      const displayName = relativePath ? relativePath.replace(`${rootFolderName}/`, '') : fileName;

      return {
        name: displayName,
        sanitizedName: sanitizeName(displayName),
        isUploaded: false,
        isUploading: false,
        progress: 0,
        type: file.type
      };
    });

    // Initialize folder upload info
    const initialFolderInfo: FolderUploadInfo = {
      name: rootFolderName,
      totalFiles: allFiles.length,
      uploadedFiles: 0,
      currentFileName: "",
      currentProgress: 0,
      currentBatch: 1,
      totalBatches: Math.ceil(allFiles.length / BATCH_SIZE),
      filesInCurrentBatch: Math.min(BATCH_SIZE, allFiles.length),
      folderContents: folderContents
    };

    setFolderUploadInfo(initialFolderInfo);

    try {
      // Step 1: Process and create folder structure
      const folderIdMap = await processAndCreateFolders(allFiles);

      // Step 2: Process files in batches
      await processFilesInBatches(fileArray, generalFilesArray, folderIdMap);

      Mixpanel.track("folder_upload_success", {
        audioFiles: fileArray.length,
        generalFiles: generalFilesArray.length,
        totalBatches: Math.ceil(allFiles.length / BATCH_SIZE)
      });

      setIsLoading(false);
      handleOpenMessageModal();
      setIsUploadProcessing(false);
    } catch (error) {
      console.error("Error during folder upload:", error);
      toast.error("Error uploading folder. Please try again.");
      setIsLoading(false);
      setIsUploadProcessing(false);
    }
  };

  return (
    <Box>
      {!folderToUpload && (
        <Box sx={batchTrackAddModalStyles.uploadButtonContainer}>
          <label htmlFor="folder_input">
            <input
              type="file"
              onChange={(e) => handleFileChange(e)}
              id="folder_input"
              multiple
              {...({
                webkitdirectory: "",
                directory: "",
              } as React.HTMLAttributes<HTMLInputElement>)}
              style={{ display: "none" }}
            />
            <Box sx={batchTrackAddModalStyles.uploadContainer}>
              <Typography>
                Note: Uploading ZIP files and empty folders is not supported.
              </Typography>
              <Button
                variant="contained"
                component="span"
                startIcon={<CloudUploadIcon />}
                sx={batchTrackAddModalStyles.uploadButton}
              >
                <Typography variant="body1" fontWeight="bold">
                  Select folder to upload
                </Typography>
              </Button>
            </Box>
          </label>
        </Box>
      )}
      {folderToUpload && (
        <Box sx={batchTrackAddModalStyles.folderNameContainer}>
          <Typography sx={{ minWidth: "90%" }}>{folderToUpload}</Typography>
          <IconButton onClick={() => handleClearFolder()}>
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      <DialogActions sx={addItemsModalStyles.buttonsContainer}>
        <CustomButton
          text="Upload Folder"
          onClick={handleFolderUpload}
          customStyles={{ width: "150px" }}
          loading={isLoading}
        />
        <CustomButton
          text="Cancel"
          onClick={handleClose}
          isSkipButton={true}
          customStyles={{ width: "150px" }}
        />
      </DialogActions>
    </Box>
  );
};

export default FolderUpload;
